import { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { ApolloQueryResult } from '@apollo/client/core/types'
import { Box } from '@factorialco/deprecated-design-system'
import { ExpensesSignedDocumentFile, GetExpensablesQuery } from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import i18n from 'modules/core/lib/i18n'

import DropItem, { UploadFile } from 'modules/expenses/components/common/DropItem'
import DropZoneBox from 'modules/expenses/components/common/DropZone'
import useUploadFileDialog from 'modules/expenses/components/expenses/Detail/_newForm/Files/hooks/useUploadFileDialog'
import FormBlock from 'modules/expenses/components/expenses/Detail/_newForm/Sections/FormBlock'
import { castError } from 'modules/expenses/components/expenses/Detail/_newForm/castError'
import withCustomizationFields, {
  WithCustomizationFieldsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import withFieldPermissions, {
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { useFilesPreviewer } from 'modules/expenses/components/expenses/Detail/hooks/useFilesPreviewer'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'

interface Props extends WithCustomizationFieldsProps, WithFieldPermissionsProps {
  refetchExpensable?: () => Promise<ApolloQueryResult<GetExpensablesQuery>>
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
}

const FIELD_NAME = 'files'
const Files = ({
  isFieldReadOnly,
  isFieldMandatory,
  expensable,
  refetchExpensable,
  taxAgencyOrganization,
  registerDropHandler,
  resourceType,
}: Props) => {
  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext()
  const previewer = useFilesPreviewer()
  const [_filesIdToRemove, setFilesIdToRemove] = useState<number[]>([])

  // Signed documents display only
  const [signedDocument, setSignedDocument] = useState<ExpensesSignedDocumentFile | null>(
    expensable?.expense?.signedDocument || null
  )

  // Sets if document is signed
  const isSignedDocument = (fileId: number) => {
    return signedDocument?.file?.id === fileId
  }

  // Management of signingDocumentStatus
  const originalSigningFiles = signedDocument?.originalFiles || []

  const isSigningDocument = (fileId: number) => {
    return originalSigningFiles.some((originalFile) => String(originalFile.id) === String(fileId))
  }

  // Handles removable
  const hasDocumentId = !!signedDocument?.id
  const hasDocumentFile = !!signedDocument?.file
  const isDocumentInSigningProcess = hasDocumentId && !hasDocumentFile

  const filesHasSignedDocument = previewer.files.some(
    (file) => Number(file.id) === signedDocument?.file?.id
  )

  const showSigning = isDocumentInSigningProcess && !filesHasSignedDocument
  const isRegularExpense = resourceType === 'expense'

  const isFileRemovable = (fileId: number, showSigningItem: boolean): boolean => {
    // Never removable during signing process
    if (showSigningItem) return false

    // For uploaded files, check if editing is allowed
    if (fileId > 0) return !isFieldReadOnly

    // For non-uploaded files, check if files field is editable
    return !isFieldReadOnly
  }

  const hasFiles = previewer.files.length > 0

  const {
    Dialog: DialogUploadFile,
    openDialog: openDialogUploadFile,
    handleJustUpload,
  } = useUploadFileDialog({
    expensable,
    taxAgencyOrganization,
    refetchExpensable,
  })

  const onDropFiles = useCallback(
    ({ files, promptOCR }: { files: File[]; promptOCR: boolean }) => {
      if (promptOCR) {
        openDialogUploadFile(files)
      } else {
        handleJustUpload(files)
      }
    },
    [openDialogUploadFile, handleJustUpload]
  )

  const onRemoveUploadFile = (removedFile: UploadFile) => {
    previewer.removeFile(removedFile)

    if (removedFile.id) {
      setFilesIdToRemove((previousIds) => {
        const newIds = [...previousIds, Number(removedFile.id)]
        setValue('filesIdToRemove', newIds, { shouldDirty: true })

        if (isSignedDocument(Number(removedFile.id))) {
          setSignedDocument(null)
        }

        return newIds
      })
    } else {
      const filteredFiles = getValues('files').filter(
        (file: UploadFile) =>
          file.name !== removedFile.name && String(file.size) !== removedFile.size
      )

      setValue('files', filteredFiles, { shouldDirty: true, shouldValidate: true })
    }
  }

  const onDropFilesRef = useRef(onDropFiles)
  onDropFilesRef.current = onDropFiles

  useEffect(() => {
    if (registerDropHandler) {
      // Hack alert: Something happens in the dropzone file that makes the handler
      // to be called inmediately so we need to wrap it with a lazy function
      const wrappedHandler = () => (files: File[]) => {
        onDropFilesRef.current({ files, promptOCR: isRegularExpense })
      }
      registerDropHandler(wrappedHandler)
    }
  }, [registerDropHandler, isRegularExpense])

  useEffect(() => {
    trigger('files')
  }, [previewer.files.length])

  const getSignedDocumentDate = (fileId: number): string | undefined => {
    if (isSignedDocument(fileId) && signedDocument?.createdAt) {
      return signedDocument.createdAt
    }
    return undefined
  }

  return (
    <FormBlock title={i18n.t('expenses_v2.forms.new_form.blocks.files')}>
      <Controller
        name={FIELD_NAME}
        control={control}
        render={() => (
          <>
            {!isFieldReadOnly && (
              <LabeledInput
                mandatory={!hasFiles && isFieldMandatory}
                error={castError(errors?.files)}
                isLegend={hasFiles || !isFieldMandatory}
              >
                <Box width="full">
                  <DropZoneBox
                    onDrop={(files) =>
                      onDropFiles({ files: files, promptOCR: isRegularExpense && !hasFiles })
                    }
                    title={i18n.t('expenses_v2.forms.fields.dropzone.title')}
                    description={i18n.t('expenses_v2.forms.fields.dropzone.description')}
                    multiple
                  />
                  {DialogUploadFile}
                </Box>
              </LabeledInput>
            )}

            {previewer?.files &&
              previewer.files.map((file: UploadFile) => (
                <DropItem
                  key={file.id || file.name + file.size}
                  file={file}
                  onSelectFileForViewer={previewer.selectFile}
                  onRemove={onRemoveUploadFile}
                  removable={isFileRemovable(Number(file.id), showSigning)}
                  signedDocument={isSignedDocument(Number(file.id))}
                  signingDocument={isSigningDocument(Number(file.id))}
                  date={getSignedDocumentDate(Number(file.id))}
                  showAdeTag={taxAgencyOrganization === 'ade'}
                  adeArchived={expensable?.expense?.adeArchived === true}
                />
              ))}
          </>
        )}
      />
    </FormBlock>
  )
}

export default withFieldPermissions(withCustomizationFields(Files), FIELD_NAME)
